import React, { useState, useEffect, useRef, useCallback } from "react";
import { Tooltip } from "bootstrap";
import axios from "axios";
import { callOperation } from "../../utils/apiHelper.js";

function HtmlContainer({ file, token }) {
  const [htmlFileUrl, setHtmlFileUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [progress, setProgress] = useState(0);
  const [maxProgress, setMaxProgress] = useState(10000); // Default maxProgress value
  const [isGeneratingStats, setIsGeneratingStats] = useState(false);
  const buttonRef = useRef(null);
  let tooltipInstance = useRef(null);

  const checkFileExists = async (url) => {
    try {
      const response = await axios.head(url);
      return response.status === 200;
    } catch (error) {
      return false;
    }
  };

  const checkProgress = useCallback(async (file) => {
    try {
      const response = await callOperation("get_progress", { file_name: file }, token);
      return {
        progress: response.data.progress,
        maxProgress: response.data.maxProgress,
        running: response.data.running,
      };
    } catch (error) {
      console.error("Error checking progress:", error);
      return { progress: 0, maxProgress: 10000, running: false };
    }
  }, [token]);

  const loadFile = useCallback(async (file) => {
    const url = `/view/${file}.html`;
    // console.log('Loading file:', url);
    setErrorMessage("");
    setIsGeneratingStats(false);

    const exists = await checkFileExists(url);
    if (exists) {
      setHtmlFileUrl(url);
    } else {
      setErrorMessage(
        `The ${file} stats was not found. Please run the stats to generate it.`
      );
    }
  }, []);

  const pollProgress = useCallback(
    (file) => {
      const progressInterval = setInterval(async () => {
        const { progress, maxProgress, running } = await checkProgress(file);
        setProgress(progress);
        setMaxProgress(maxProgress);

        if (!running) {
          clearInterval(progressInterval); // Stop polling when not running
          loadFile(file); // Load the file if stats are generated
          setIsGeneratingStats(false);
        }
      }, 2000);

      return progressInterval;
    },
    [loadFile, checkProgress]
  );

  const runStats = async () => {
    // Check if stats are currently running
    const { running } = await checkProgress(file);

    if (running) {
      setIsGeneratingStats(true);
      const intervalId = pollProgress(file); // Start polling for progress
      return intervalId;
    } else {
      setIsGeneratingStats(true);
      setProgress(0);

      try {
        await callOperation("run_stats", { statsType: file }, token);
        const intervalId = pollProgress(file); // Start polling for progress
        return intervalId;
      } catch (error) {
        console.error("Error while running stats:", error);
        setIsGeneratingStats(false);
        setErrorMessage(
          "An error occurred while running the stats. Please try again."
        );
        return null;
      }
    }
  };

  useEffect(() => {
    let intervalId = null;
    let mounted = true;

    const initializeComponent = async () => {
      if (!file || !mounted) return;

      // First check if stats are currently running
      const { progress, maxProgress, running } = await checkProgress(file);

      if (!mounted) return;

      setMaxProgress(maxProgress);
      setProgress(progress);

      if (running) {
        setIsGeneratingStats(true);
        intervalId = pollProgress(file);
      } else {
        // Only check file existence if not already running
        const url = `/view/${file}.html`;
        const exists = await checkFileExists(url);

        if (!mounted) return;

        if (exists) {
          setHtmlFileUrl(url);
        } else {
          setErrorMessage(
            `The ${file} stats was not found. Please run the stats to generate it.`
          );
        }
      }
    };

    initializeComponent();

    // Initialize tooltip
    if (buttonRef.current) {
      tooltipInstance.current = new Tooltip(buttonRef.current, {
        title: `If the stats of ${file} do not exist, please run the stats.`,
        placement: "right",
        boundary: "viewport",
        trigger: "hover focus",
        delay: { show: 300, hide: 100 },
      });
    }

    return () => {
      mounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (tooltipInstance.current) {
        tooltipInstance.current.dispose();
      }
    };
  }, [file, pollProgress, checkProgress]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      {!errorMessage && !isGeneratingStats && (
        <iframe
          title="HTML Content"
          src={htmlFileUrl}
          style={{ width: "100%", height: "100%" }}
        />
      )}

      {errorMessage && (
        <>
          <div className="alert alert-warning text-center" role="alert">
            {errorMessage}
          </div>
          <div className="mb-3 text-center">
            <button
              onClick={runStats}
              className="btn btn-primary"
              ref={buttonRef}
              disabled={isGeneratingStats} // Disable button while stats are generating
            >
              Run Stats for {file}
            </button>
          </div>
        </>
      )}

      {/* Show progress bar if stats are generating */}
      {isGeneratingStats && (
        <div className="text-center">
          <h5>Generating stats for {file}...</h5>
          <div
            className="progress"
            style={{ height: "30px", width: "50%", margin: "0 auto" }}
          >
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: `${(progress / maxProgress) * 100}%` }}
              aria-valuenow={progress}
              aria-valuemin="0"
              aria-valuemax={maxProgress}
            >
              {Math.round((progress / maxProgress) * 100)}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HtmlContainer;
